import React, { useEffect, useState } from "react";

/**
 * LiveStat — deterministic, time-based counter that grows by [min..max]
 * every 15 minutes since `anchor`. Same value for everyone visiting at the
 * same time (no jitter between page loads), with a soft "+1" tick while the
 * user is on the page for live-feel social proof.
 *
 * Stats anchored at 2026-02-05T00:00:00Z (today). Update the anchor date
 * any day to "reset" the live baseline.
 */
const FIFTEEN_MIN_MS = 15 * 60 * 1000;
// Anchor: update this date daily/weekly to refresh the live baseline. From
// this date onward, every 15-min window adds [min..max] to the count, and
// the math is deterministic so every visitor sees the same number at the
// same moment.
export const STATS_ANCHOR_MS = new Date("2026-09-29T00:00:00Z").getTime();

function ticksPerWindow(windowIndex, min, max, salt) {
  const range = max - min + 1;
  // Deterministic hash → integer in [min, max]
  const h = (((windowIndex + salt) * 2654435761) >>> 0) % range;
  return min + h;
}

/**
 * joinedInLastHour — sums the last 4 fifteen-minute windows of active-member
 * growth. Always returns a sensible positive number (fallback of 11) so the
 * UI never shows "0 joined in the last hour" if the anchor is in the future.
 */
export function joinedInLastHour({ min = 2, max = 5, salt = 1, anchor = STATS_ANCHOR_MS } = {}) {
  const elapsed = Math.max(0, Date.now() - anchor);
  const currentWindow = Math.floor(elapsed / FIFTEEN_MIN_MS);
  let total = 0;
  for (let i = Math.max(0, currentWindow - 4); i < currentWindow; i++) {
    total += ticksPerWindow(i, min, max, salt);
  }
  return total === 0 ? 11 : total;
}

function computeCount({ base, min, max, anchor, salt }) {
  const elapsed = Math.max(0, Date.now() - anchor);
  const fullWindows = Math.floor(elapsed / FIFTEEN_MIN_MS);
  let total = base;
  for (let i = 0; i < fullWindows; i++) {
    total += ticksPerWindow(i, min, max, salt);
  }
  return total;
}

export function LiveStat({
  base,
  min = 2,
  max = 5,
  anchor = STATS_ANCHOR_MS,
  salt = 0,
  suffix = "",
  liveTick = true,
  className = "",
  testid = "live-stat",
}) {
  const [count, setCount] = useState(() =>
    computeCount({ base, min, max, anchor, salt })
  );

  // Recompute every 30s so the count crosses 15-min boundaries cleanly
  useEffect(() => {
    const id = setInterval(() => {
      setCount(computeCount({ base, min, max, anchor, salt }));
    }, 30000);
    return () => clearInterval(id);
  }, [base, min, max, anchor, salt]);

  // Soft "+1" tick every 90-180s for live feel
  useEffect(() => {
    if (!liveTick) return;
    let id;
    const schedule = () => {
      const delay = 90000 + Math.random() * 90000;
      id = setTimeout(() => {
        setCount((c) => c + 1);
        schedule();
      }, delay);
    };
    schedule();
    return () => clearTimeout(id);
  }, [liveTick]);

  return (
    <span
      className={`tabular-nums inline-flex items-center ${className}`}
      data-testid={testid}
      aria-label={`${count.toLocaleString()}${suffix}`}
    >
      <span
        key={count}
        className="inline-block animate-in fade-in slide-in-from-bottom-1 duration-500"
      >
        {count.toLocaleString()}
        {suffix}
      </span>
    </span>
  );
}

export default function LiveActiveMembers({ className = "" }) {
  return (
    <LiveStat
      base={2500}
      min={2}
      max={5}
      salt={1}
      className={className}
      testid="live-active-members"
    />
  );
}
